import React from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Tooltip,
    Typography,
    Box,
    Avatar,
    Skeleton,
} from "@mui/material";
import {
    Visibility as ViewIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    LocationOn as LocationIcon,
    Person as PersonIcon,
} from "@mui/icons-material";

const CustomerMasterTable = ({ data = [], loading = false, onViewDetails, pagination, onPaginationChange }) => {
    const getStatusColor = (isActive) => {
        return isActive ? "success" : "error";
    };

    const getStatusLabel = (isActive) => {
        return isActive ? "Active" : "Inactive";
    };

    if (loading) {
        return (
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Customer</TableCell>
                            <TableCell>Contact</TableCell>
                            <TableCell>Location</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Interactions</TableCell>
                            <TableCell>Last Activity</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {[...Array(5)].map((_, index) => (
                            <TableRow key={index}>
                                <TableCell>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Skeleton variant="circular" width={40} height={40} />
                                        <Box>
                                            <Skeleton variant="text" width={120} height={20} />
                                            <Skeleton variant="text" width={80} height={16} />
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="text" width={100} height={20} />
                                    <Skeleton variant="text" width={120} height={16} />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="text" width={80} height={20} />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="rectangular" width={100} height={24} />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="text" width={60} height={20} />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="text" width={80} height={20} />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="circular" width={32} height={32} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    if (!data || data.length === 0) {
        return (
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Customer</TableCell>
                            <TableCell>Contact</TableCell>
                            <TableCell>Location</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Interactions</TableCell>
                            <TableCell>Last Activity</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                <Typography variant="body1" color="text.secondary">
                                    No customers found
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    return (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Customer</TableCell>
                        <TableCell>Contact</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Interactions</TableCell>
                        <TableCell>Last Activity</TableCell>
                        <TableCell>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.map((customer) => (
                        <TableRow key={customer.id} hover>
                            <TableCell>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Avatar sx={{ width: 40, height: 40, bgcolor: "primary.main" }}>
                                        <PersonIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight="bold">
                                            {customer.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            ID: {customer.id}
                                        </Typography>
                                    </Box>
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                                    <PhoneIcon fontSize="small" color="action" />
                                    <Typography variant="body2">{customer.mobile}</Typography>
                                </Box>
                                {customer.email && (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <EmailIcon fontSize="small" color="action" />
                                        <Typography variant="body2" color="text.secondary">
                                            {customer.email}
                                        </Typography>
                                    </Box>
                                )}
                            </TableCell>
                            <TableCell>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <LocationIcon fontSize="small" color="action" />
                                    <Box>
                                        <Typography variant="body2">{customer.city}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {customer.state} - {customer.pincode}
                                        </Typography>
                                    </Box>
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Chip
                                    label={getStatusLabel(customer.is_active)}
                                    color={getStatusColor(customer.is_active)}
                                    size="small"
                                />
                            </TableCell>
                            <TableCell>
                                <Box>
                                    <Typography variant="body2" fontWeight="bold">
                                        {customer.stats?.total_interactions || 0}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Orders: {customer.stats?.orders_count || 0} | Warranty:{" "}
                                        {customer.stats?.warranty_registrations_count || 0} | Insurance:{" "}
                                        {customer.stats?.insurance_renewals_count || 0}
                                    </Typography>
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2">
                                    {customer.created_at ? new Date(customer.created_at).toLocaleDateString() : "N/A"}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Tooltip title="View Details">
                                    <IconButton size="small" onClick={() => onViewDetails(customer)} color="primary">
                                        <ViewIcon />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default CustomerMasterTable;
